import { styled } from 'styled-components';
import { isMobile } from 'react-device-detect';

import Sketch from 'react-p5';

import { useWindowSize } from '@uidotdev/usehooks';
import { useEffect, useRef, useLayoutEffect, useState } from 'react';
import p5 from 'p5';
import p5Card from '@src/components/Game/Handv2/p5Card';

import {
  draw as drawLayout,
  setup as setupLayout,
  p5GetCards,
  onResized,
  teardown as teardownLayout,
} from '@src/components/Game/Handv2/p5Layout';

import { Card as CardType } from '@src/lib/types';

import _playCard from '@src/io/events_out/_playCard';

// import { v4 as uuidv4 } from 'uuid';

const Container = styled.div`
  display: flex;  
  flex-direction: row;
  // justify-content: 
  align-items: center;
  flex-wrap: none;

  flex: 1;
  
  width: 100%;
  max-height: 30%;
  min-height: 15%;
  min-width: 0;

  padding-bottom: 1rem;
  padding-top: 1rem;
  scrollbar-color: #c0c0c0 #c0c0c0;
  overflow-y: hidden;
  overflow-x: hidden;

  // background-color: #c0c0c0;

`;

type Props = {
  gameId: string | null;
  isPlayersTurn: boolean;
  cards: CardType[] | null;
  setShouldPlaySound: (setShouldPlaySound: boolean) => void;
};

// const cards = [
//   {
//     cardId: uuidv4(),
//     value: 1,
//     color: 'white',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 2,
//     color: 'red',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 3,
//     color: 'blue',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 4,
//     color: 'green',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 1,
//     color: 'black',
//     isSpecial: true,
//   },
//   {
//     cardId: uuidv4(),
//     value: 1,
//     color: 'white',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 2,
//     color: 'red',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 3,
//     color: 'blue',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 4,
//     color: 'green',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 1,
//     color: 'black',
//     isSpecial: true,
//   },
//   {
//     cardId: uuidv4(),
//     value: 1,
//     color: 'white',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 2,
//     color: 'red',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 3,
//     color: 'blue',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 4,
//     color: 'green',
//     isSpecial: false,
//   },
//   {
//     cardId: uuidv4(),
//     value: 1,
//     color: 'black',
//     isSpecial: true,
//   },
// ];

export default function Hand(props: Props) {
  const containerSize = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [p5ref, setP5ref] = useState<p5 | null>(null);
  const [p5Cards, setP5Cards] = useState<p5Card[] | null>(null);
  const propsRef = useRef<Props>(props);

  const cards = props.cards ? props.cards : [];

  const windowSize = useWindowSize();

  // track container size
  useLayoutEffect(() => {
    const e = containerRef.current;
    if (!e) return;
    containerSize.current = {
      width: e.offsetWidth,
      height: e.offsetHeight,
    };
  }, [
    windowSize.width,
    windowSize.height,
    containerRef.current?.offsetWidth,
    containerRef.current?.offsetHeight,
    isMobile,
  ]);

  // resize canvas when container size changes
  useEffect(() => {
    if (p5ref === null) return;
    onResized(p5ref, containerSize.current.width, containerSize.current.height);
  }, [containerSize.current]);

  // setup layout when component mounts
  // call layout teardown on component dismount
  useEffect(() => {
    if (p5ref === null) return;
    setupLayout(p5ref, handleCardClicked);
    setP5Cards(p5GetCards(p5ref, cards));

    return () => {
      teardownLayout(p5ref);
    };
  }, [p5ref]);

  // when cards prop updates, update p5 card objects
  useEffect(() => {
    if (p5ref === null) return;
    if (cards === null) return;
    setP5Cards(p5GetCards(p5ref, cards));
  }, [props.cards]);

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  const playCard = (card: CardType) => {
    if (!propsRef.current.isPlayersTurn || !propsRef.current.gameId) return;
    props.setShouldPlaySound(true);
    _playCard(propsRef.current.gameId, card.cardId);
  };

  const handleCardClicked = (card: CardType) => {
    playCard(card);
  };

  // p5 setup function
  const setup = (p5: p5, canvasParentRef: Element) => {
    setP5ref(p5);
    p5.createCanvas(
      containerSize.current.width,
      containerSize.current.height,
    ).parent(canvasParentRef);
  };

  const draw = (p5: p5) => {
    // clear canvas before drawing
    p5.clear(0, 0, 0, 0);
    // p5.background(51, 180); // DEBUG: show background
    // p5.fill(255); // DEBUG: show framerate
    // p5.text(Math.round(p5.frameRate()), 100, 100);

    drawLayout(p5, p5Cards || []);
  };

  return (
    <Container id='game-hand' ref={containerRef}>
      {/* @ts-ignore sorry */}
      <Sketch setup={setup} draw={draw} />
    </Container>
  );
}
